/* eslint-disable max-len */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Switch, Route, useRouteMatch, useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Panel } from 'primereact/panel';
import OrganizationDetailsStep from './OrganizationDetailsStep';
import OtherUsersStep from './OtherUsersStep';
import OwnerRegistrationStep from './OwnerRegistrationStep';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { notificationMessages } from '../../../i18n/common';
import { clearCredentialsSession, persistCredentialsSession } from '../../../services/auth/auth-storage';
import runAcessTokenChecker from '../../../services/auth/auth-scheduler';
import { clearLoggedAccount } from '../../../reducers/account';

const AccountInvitationForm = ({
  invitation,
  countries,
  locales,
  timeZones,
  taxIds,
  token,
  userExists,
  acceptInvitation,
}) => {
  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);
  const history = useHistory();
  const { path } = useRouteMatch();

  const {
    cldr: { localeDisplayName, teritoryDisplayNames },
  } = useSelector((state) => state.i18n);

  const { fullName, email, type, availableRoles, ownerInvoiceCountry } = invitation;

  const userRegistrationCountryOptions = teritoryDisplayNames(countries);
  const companyCountryOptions = teritoryDisplayNames(countries);
  const supportedLanguages = locales.map((lang) => ({
    value: lang,
    label: localeDisplayName(lang),
  }));
  const availableTimeZones = timeZones.map((timeZone) => ({
    value: timeZone,
    label: timeZone,
  }));
  const taxIdOptions = taxIds.map((taxId) => ({
    value: taxId.id,
    label: `${taxId.country} - ${taxId.description}`,
  }));

  const buildInitialValues = () => {
    const ownerFirstAndLastName = fullName.split(' ');

    const ownerFirstName = ownerFirstAndLastName.length > 0 ? ownerFirstAndLastName[0] : ownerFirstAndLastName;

    const ownerLastName = ownerFirstAndLastName.length > 0 ? ownerFirstAndLastName[1] : ownerFirstAndLastName;

    return {
      owner: {
        firstName: ownerFirstName,
        lastName: ownerLastName,
        email,
        password: '',
        confirmPassword: '',
        locale: '',
        country: '',
        timeZone: '',
      },
      company: {
        displayName: '',
        legalName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        country: ownerInvoiceCountry || '',
        postalCode: '',
        registrationNumber: '',
        taxIdType: '',
        taxId: '',
        state: '',
        dpoEmail: '',
        creditNotificationThreshold: 1,
      },
      inviteSenderAsExternalSupport: false,
      user_terms_of_use_agreement: false,
      client_terms_of_use_agreement: false,
      gp_invoice_address_confirmation: false,
      additionalUsers: [],
    };
  };

  const validationSchema = Yup.object().shape({});

  const initialValues = buildInitialValues();

  const extractInput = (values) => {
    const { owner } = values;

    if (userExists) {
      return {
        token,
        type,
        owner: null,
        organisationSize: values.organisationSize,
        company: {
          ...values.company,
        },
        additionalUsers: values.additionalUsers,
        inviteSenderAsExternalSupport: values.inviteSenderAsExternalSupport,
      };
    }

    return {
      token,
      type,
      owner: {
        userInvitationToken: token,
        firstName: owner.firstName,
        lastName: owner.lastName,
        email: owner.email,
        password: owner.password,
        locale: owner.locale,
        country: owner.country,
        timeZone: owner.timeZone,
      },
      organisationSize: values.organisationSize,
      company: {
        ...values.company,
      },
      additionalUsers: values.additionalUsers,
      inviteSenderAsExternalSupport: values.inviteSenderAsExternalSupport,
    };
  };

  const onHandleSubmit = async (values, { setSubmitting }) => {
    const input = extractInput(values, userExists);

    try {
      setSubmitting(true);
      const result = await acceptInvitation.mutation({
        variables: {
          input,
        },
      });

      const { data } = result;
      const { acceptClientInvitation } = data;
      const { userId, createdAccountId, tokens } = acceptClientInvitation;

      growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });

      if (!userExists) {
        history.push(`/register-confirmation?userId=${userId}`);
      } else {
        clearLoggedAccount();
        clearCredentialsSession();

        const { token: authenticationToken, refreshToken } = tokens;

        persistCredentialsSession(authenticationToken, refreshToken);
        runAcessTokenChecker();

        history.push(`/accounts/${createdAccountId}`);
      }
    } catch (err) {
      growl.show({ severity: 'error', detail: err.message, sticky: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-grid p-justify-center">
      <div className="p-sm-12 p-md-10 p-lg-8">
        <Panel>
          <div className="p-grid">
            <div className="p-sm-12">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, formikBag) => {
                  onHandleSubmit(values, formikBag);
                }}
              >
                {({ handleSubmit }) => {
                  return (
                    <Form onSubmit={handleSubmit}>
                      <Switch>
                        <Route
                          exact
                          path={`${path}`}
                          component={() => {
                            if (userExists) {
                              return (
                                <OrganizationDetailsStep
                                  invitation={invitation}
                                  countries={companyCountryOptions}
                                  availableTaxIds={taxIds}
                                  taxIdOptions={taxIdOptions}
                                  token={token}
                                  userExists={userExists}
                                  ownerInvoiceCountry={ownerInvoiceCountry}
                                />
                              );
                            } else {
                              return (
                                <OwnerRegistrationStep
                                  countries={userRegistrationCountryOptions}
                                  locales={supportedLanguages}
                                  timeZones={availableTimeZones}
                                  token={token}
                                />
                              );
                            }
                          }}
                        />
                        <Route
                          exact
                          path={`${path}/organisation-details`}
                          component={() => (
                            <OrganizationDetailsStep
                              invitation={invitation}
                              countries={companyCountryOptions}
                              availableTaxIds={taxIds}
                              taxIdOptions={taxIdOptions}
                              token={token}
                              userExists={userExists}
                              ownerInvoiceCountry={ownerInvoiceCountry}
                            />
                          )}
                        />

                        <Route
                          exact
                          path={`${path}/other-users`}
                          component={() => <OtherUsersStep type={type} availableRoles={availableRoles} />}
                        />
                      </Switch>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};

AccountInvitationForm.propTypes = {
  token: PropTypes.string.isRequired,
  userExists: PropTypes.bool.isRequired,
  invitation: PropTypes.objectOf(PropTypes.any).isRequired,
  countries: PropTypes.arrayOf(PropTypes.any).isRequired,
  locales: PropTypes.arrayOf(PropTypes.any).isRequired,
  timeZones: PropTypes.arrayOf(PropTypes.any).isRequired,
  taxIds: PropTypes.arrayOf(PropTypes.any).isRequired,
  acceptInvitation: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AccountInvitationForm;
